/* eslint-disable class-methods-use-this */

import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { ProposalDAOInterface, ProposalTextsDAOInterface } from '../ProposalDAO';
import BaseTextDAO from './BaseTextDAO';
import * as ProposalNS from '@/modules/proposal/Proposal';
import * as ProposalElementNS from '@/modules/proposal/ProposalElements';
import { formatCurrency, formatLocalizedString } from '@/utils/i18n';
import { getPortfolioValue } from '@/modules/proposal/utils/client-level-summary';
import ComparisonItem from '@/types/comparison-item';
import LocalizedString from '@/types/localized-string';
import { AccountSetRecommendation } from '@/types/comparison-account-set';
import Icon from '@/views/components/common/Icon';

type AccountSetPortfolioValueRow = {
  label: string;
  name: string;
  value: number;
  color?: string;
  iconSvg: string;
};

type AccountSetPortfolioValueConfig = ProposalNS.AccountSetPortfolioValueConfig;

type PortfolioEntry = {
  label: string;
  item?: ComparisonItem;
  isCurrent?: boolean;
  isPrimary?: boolean;
};

export default class AccountSetPortfolioValueDAO extends BaseTextDAO implements ProposalTextsDAOInterface {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new AccountSetPortfolioValueDAO(proposal, proposalElement);
  }

  private accountSetIndex = 0;

  private title?: LocalizedString;

  private className?: string;

  protected getColorKeys(): string[] {
    return [];
  }

  protected build(): void {
    const daoConfig: AccountSetPortfolioValueConfig = this.proposalElement.DAO as AccountSetPortfolioValueConfig;

    this.accountSetIndex = daoConfig.accountSetIndex ?? 0;
    this.title = daoConfig.title;
    this.className = daoConfig.className;
  }

  private getAccountSetRows(): AccountSetPortfolioValueRow[] {
    const rows: AccountSetPortfolioValueRow[] = [];
    const accountSet = this.proposal.accountSets?.[this.accountSetIndex];
    const { locale } = this.proposal.options;

    if (!accountSet) {
      return rows;
    }

    const entries: PortfolioEntry[] = [];
    const currentLabel = this.formatMessage('Comparison.AccountSets.Row.Current');

    if (accountSet.current !== undefined && accountSet.current >= 0) {
      entries.push({
        label: currentLabel,
        item: this.proposal.items[accountSet.current],
        isCurrent: true,
      });
    }

    this.orderRecommendations(accountSet.recommendations).forEach(recommendation => {
      const label = recommendation.label ? formatLocalizedString(recommendation.label, locale) : this.getFallbackRecommendationLabel(recommendation.isPrimary);
      entries.push({
        label,
        item: recommendation.itemId >= 0 ? this.proposal.items[recommendation.itemId] : undefined,
        isPrimary: recommendation.isPrimary,
      });
    });

    const missingLabel = this.formatMessage('Comparison.AccountSets.Validation.MissingPortfolio');

    entries.forEach(entry => {
      const name = entry.item ? formatLocalizedString(entry.item.name, locale) : missingLabel;
      const value = entry.item ? getPortfolioValue(entry.item) : 0;

      rows.push({
        label: entry.label,
        name,
        value,
        color: entry.item?.color,
        iconSvg: this.getEntryIconSvg(entry),
      });
    });

    return rows;
  }

  private orderRecommendations(recommendations: AccountSetRecommendation[] = []): AccountSetRecommendation[] {
    const primary = recommendations.filter(recommendation => recommendation.isPrimary);
    const alternatives = recommendations.filter(recommendation => !recommendation.isPrimary);

    return [...primary, ...alternatives];
  }

  private getFallbackRecommendationLabel(isPrimary: boolean): string {
    return this.formatMessage(isPrimary ? 'Comparison.AccountSets.Row.Recommended' : 'Comparison.AccountSets.Row.RecommendedOptional');
  }

  private getEntryIconSvg(entry: PortfolioEntry): string {
    let iconClass: string;

    if (entry.isCurrent) {
      iconClass = 'icon-ds-star';
    } else if (entry.isPrimary) {
      iconClass = 'icon-ds-radio-button-selected';
    } else {
      iconClass = 'icon-ds-radio-button-partial';
    }
    const iconType = iconClass.replace(/^icon-/, '');

    return ReactDOMServer.renderToString(
      React.createElement(Icon, {
        type: iconType,
        size: 12,
        className: 'account-set-portfolio-value-item-icon',
        'aria-hidden': true,
      }),
    );
  }

  getTexts(): ProposalNS.ProposalText[] {
    const out: ProposalNS.ProposalText[] = [];
    const { locale } = this.proposal.options;
    const rows = this.getAccountSetRows();

    const totalValue = rows.reduce((sum, row) => sum + row.value, 0);
    const formattedTotal = formatCurrency(totalValue, 2, undefined, true, locale);

    const summaryHtml = this.title
      ? `
        <div class="account-set-portfolio-value-summary">
          <div class="account-set-portfolio-value-summary-label">${formatLocalizedString(this.title, locale)}</div>
          <div class="account-set-portfolio-value-summary-total">${formattedTotal}</div>
        </div>
      `
      : '';

    const rowsHtml = rows
      .map(row => {
        const borderColor = row.color || '#86888a';

        return `
          <div class="account-set-portfolio-value-item" style="border-left-color: ${borderColor}">
            <div class="account-set-portfolio-value-item-content">
              <div class="account-set-portfolio-value-item-label">
                ${row.iconSvg}
                <span>${row.label}</span>
              </div>
              <div class="account-set-portfolio-value-item-name">${row.name}</div>
            </div>
          </div>
        `;
      })
      .join('');

    const htmlContent = `
      <div class="account-set-portfolio-value">
        ${summaryHtml}
        <div class="account-set-portfolio-value-list">
          ${rowsHtml}
        </div>
      </div>
    `;

    out.push({
      text: htmlContent,
      className: this.className || 'account-set-portfolio-value-container',
    });

    return out;
  }

  getData(): unknown[] {
    return [];
  }
}
